import { useRef, useState } from 'react';

export interface Slide {
  src: string;
  alt: string;
  label: string;
}

export default function ScreenshotCarousel({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const previous = () => setIndex((current) => (current - 1 + slides.length) % slides.length);
  const next = () => setIndex((current) => (current + 1) % slides.length);

  return (
    <div
      className="screenshot-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="DJINN app screenshots"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') previous();
        if (event.key === 'ArrowRight') next();
      }}
      onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
      onTouchEnd={(event) => {
        if (touchStart.current === null) return;
        const distance = (event.changedTouches[0]?.clientX ?? 0) - touchStart.current;
        if (distance > 45) previous();
        if (distance < -45) next();
        touchStart.current = null;
      }}
    >
      <div className="carousel-stage">
        {slides.map((slide, slideIndex) => {
          const position = slideIndex === index ? 'active' : slideIndex === (index - 1 + slides.length) % slides.length ? 'previous' : slideIndex === (index + 1) % slides.length ? 'next' : 'hidden';
          return (
            <div className={`carousel-phone carousel-phone-${position}`} key={slide.src} aria-hidden={position !== 'active'}>
              {/* Hidden slides are display:none, and a lazy image there is never
                  fetched — so a slide started downloading only once you clicked
                  to it, showing an empty phone first. Loading the neighbours
                  keeps the next click one step ahead. */}
              <img src={slide.src} alt={position === 'active' ? slide.alt : ''} loading={position === 'hidden' ? 'lazy' : 'eager'} />
            </div>
          );
        })}
        <button type="button" className="carousel-arrow carousel-arrow-left" aria-label="Previous screenshot" onClick={previous}>‹</button>
        <button type="button" className="carousel-arrow carousel-arrow-right" aria-label="Next screenshot" onClick={next}>›</button>
      </div>
      <div className="carousel-caption" aria-live="polite">
        <span>{String(index + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
        <h3>{slides[index].label}</h3>
      </div>
      <div className="carousel-dots" aria-label="Choose screenshot">
        {slides.map((slide, slideIndex) => (
          <button
            key={slide.src}
            type="button"
            aria-label={`Show ${slide.label}`}
            aria-current={slideIndex === index ? 'true' : undefined}
            onClick={() => setIndex(slideIndex)}
          />
        ))}
      </div>
    </div>
  );
}
